import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useFetcher } from '@remix-run/react';
import { useLatest } from 'ahooks';
import dayjs from 'dayjs';
import {
    CONVERSATION_API,
    type loader,
} from '~/routes/remix-api.chat.conversation';
import {
    CONVERSATION_MESSAGE_API,
    type loader as messageLoader,
} from '~/routes/remix-api.chat.message';
import {
    CREATE_CONVERSATION_MESSAGE_API,
    type action as createMessageAction,
} from '~/routes/remix-api.chat.create-conversation-message';
import {
    MARK_CONVERSATION_READ_API,
    type action as markConversationReadAction,
} from '~/routes/remix-api.chat.mark-conversation-read';
import {
    DELETE_MESSAGE_API,
    type action as deleteMessageAction,
} from '~/routes/remix-api.chat.delete-message';
import { useUserLoggedIn } from '~/modules/user';
import type { Conversation, Message, TextOrFileMessage } from '../types';
import {
    convertToDeletedMessage,
    createOptimisticMessage,
    mergeConversations,
} from '../utils';
import { useConversationCache } from './use-conversation-cache';
import { useRealTimeChat } from './use-real-time-chat';

/**
 * Hook to get the conversation between the user and another participant(s).
 */
export function useConversation({
    id,
    pollingIntervalMs = 0,
    onDeleteSuccess,
    onDeleteError,
}: {
    /**
     * The conversation ID. If set to `0`, this hook will be disabled and the
     * conversation will not be fetched.
     */
    id: number;
    /**
     * The polling interval in milliseconds. Set to `0` to disable polling.
     * Defaults to `0` (disabled).
     */
    pollingIntervalMs?: number;
    /**
     * Callback when a message is deleted successfully.
     */
    onDeleteSuccess?: (message: Message) => void;
    /**
     * Callback when a message deletion fails.
     */
    onDeleteError?: (message: string) => void;
}) {
    const { user, isLoggedIn } = useUserLoggedIn();
    const { getCachedConversation, cacheConversation } = useConversationCache();
    const isFetched = useRef(false);
    const [conversation, setConversation] = useState<Conversation | null>(
        getCachedConversation(id) ?? null
    );
    const [optimisticMessages, setOptimisticMessages] = useState<Message[]>([]);
    const fetcher = useFetcher<typeof loader>();
    const messageFetcher = useFetcher<typeof messageLoader>();
    const createMessageFetcher = useFetcher<typeof createMessageAction>();
    const markAsReadFetcher = useFetcher<typeof markConversationReadAction>();
    const deleteFetcher = useFetcher<typeof deleteMessageAction>();
    const latestOnDeleteSuccess = useLatest(onDeleteSuccess);
    const latestOnDeleteError = useLatest(onDeleteError);
    const isHookDisabled = id <= 0 || !isLoggedIn;
    const messages = useMemo(() => {
        const serverMessages = conversation?.Messages.nodes ?? [];
        return [...optimisticMessages, ...serverMessages];
    }, [conversation, optimisticMessages]);
    const apiUrl = useMemo(() => {
        const url = new URL(CONVERSATION_API, window.location.origin);
        url.searchParams.set('id', String(id));
        return `${url.pathname}${url.search}`;
    }, [id]);
    const loadMoreUrl = useMemo(() => {
        if (
            !conversation?.Messages.nodes.length ||
            !conversation.Messages.pageInfo?.hasNextPage
        ) {
            return null;
        }
        const url = new URL(CONVERSATION_API, window.location.origin);
        url.searchParams.set('id', String(id));
        url.searchParams.set(
            'beforeID',
            conversation.Messages.nodes.at(-1)!.ID
        );
        return `${url.pathname}${url.search}`;
    }, [id, conversation]);
    useRealTimeChat({
        onMessage: (message) => {
            if (message.conversation_id === id) {
                if (
                    message.type === 'chat/message' ||
                    message.type === 'chat/update'
                ) {
                    refetch();
                } else if (message.type === 'chat/message/updated') {
                    // Just update the message if a message was updated.
                    messageFetcher.load(
                        `${CONVERSATION_MESSAGE_API}?id=${message.message_id}`
                    );
                }
            }
        },
    });

    const refetch = useCallback(() => {
        if (isHookDisabled) return;
        fetcher.load(apiUrl);
    }, [isHookDisabled, fetcher, apiUrl]);

    const loadMore = useCallback(() => {
        if (fetcher.state !== 'idle' || !loadMoreUrl) {
            return;
        }
        fetcher.load(loadMoreUrl);
    }, [fetcher, loadMoreUrl]);

    const createMessage = useCallback(
        ({
            message,
            attachments,
            replyTo,
        }: {
            message?: string;
            attachments?: File[];
            replyTo?: TextOrFileMessage;
        }) => {
            if (isHookDisabled || !isLoggedIn) return;
            // TODO: tracking
            const formData = new FormData();
            formData.append('conversationID', String(id));
            if (message) {
                formData.append('message', message);
            }
            if (attachments?.length) {
                attachments.forEach((attachment) => {
                    formData.append('attachments', attachment);
                });
            }
            if (replyTo) {
                formData.append('replyToID', String(replyTo.ID));
            }
            createMessageFetcher.submit(formData, {
                action: CREATE_CONVERSATION_MESSAGE_API,
                method: 'POST',
                encType: attachments?.length
                    ? 'multipart/form-data'
                    : undefined,
            });
            setOptimisticMessages((prev) => {
                const newMessage = createOptimisticMessage({
                    conversationID: id,
                    message,
                    attachments,
                    replyTo,
                    conversation,
                    user,
                });
                return [newMessage, ...prev];
            });
        },
        [
            isHookDisabled,
            isLoggedIn,
            conversation,
            id,
            user,
            createMessageFetcher,
        ]
    );

    const markAsRead = useCallback(() => {
        if (isHookDisabled) return;
        markAsReadFetcher.submit(
            { conversationID: id },
            {
                action: MARK_CONVERSATION_READ_API,
                method: 'POST',
                encType: 'application/json',
            }
        );
    }, [isHookDisabled, id, markAsReadFetcher]);

    const updateMessage = useCallback(
        (message: Message) => {
            setConversation((prev) => {
                if (!prev?.Messages.nodes.some((m) => m.ID === message.ID)) {
                    return prev;
                }
                const updatedConversation = {
                    ...prev,
                    Messages: {
                        ...prev.Messages,
                        // @ts-expect-error
                        nodes: [message],
                    },
                } satisfies Conversation as Conversation;
                // Need to use this function to update the message with the correct types.
                const merged = mergeConversations(prev, updatedConversation);
                if (merged) {
                    cacheConversation(merged);
                }
                return merged;
            });
        },
        [cacheConversation]
    );

    const deleteMessage = useCallback(
        (messageID: number) => {
            deleteFetcher.submit(
                { messageID },
                {
                    action: DELETE_MESSAGE_API,
                    method: 'POST',
                    encType: 'application/json',
                }
            );
            const deletedMessage = messages.find(
                (m) => m.ID === String(messageID)
            );
            if (deletedMessage) {
                updateMessage(convertToDeletedMessage(deletedMessage));
            }
        },
        [deleteFetcher, messages, updateMessage]
    );

    // Load the conversation details.
    useEffect(() => {
        if (isHookDisabled) {
            setConversation(null);
            setOptimisticMessages([]);
            isFetched.current = false;
        } else if (fetcher.state === 'idle' && !isFetched.current) {
            // Fetch the conversation on mount.
            fetcher.load(apiUrl);
            isFetched.current = true;
        } else if (fetcher.data && fetcher.state === 'idle') {
            // The new fetcher data might be a different page, so merge the
            // messages instead of replacing the conversation.
            setConversation((prev) => {
                const merged = mergeConversations(
                    prev,
                    fetcher.data!.conversation
                );
                if (merged) {
                    cacheConversation(merged);
                }
                return merged;
            });
            if (fetcher.state === 'idle') {
                setOptimisticMessages([]);
            }
        }
    }, [isHookDisabled, fetcher, apiUrl, cacheConversation]);

    // Use the cached conversation if it exists.
    useEffect(() => {
        if (isHookDisabled) return;
        const cachedConversation = getCachedConversation(id);
        if (cachedConversation) {
            setConversation(cachedConversation);
        }
    }, [isHookDisabled, id, getCachedConversation]);

    // Polling to update the conversation details.
    useEffect(() => {
        if (isHookDisabled || pollingIntervalMs <= 0) {
            return;
        }
        const interval = setInterval(() => {
            fetcher.load(apiUrl);
        }, pollingIntervalMs);
        return () => clearInterval(interval);
    }, [isHookDisabled, pollingIntervalMs, fetcher, apiUrl]);

    // Update the message after fetching from the server.
    useEffect(() => {
        if (messageFetcher.data && messageFetcher.state === 'idle') {
            updateMessage(messageFetcher.data.message);
        }
    }, [messageFetcher, updateMessage]);

    // Update the optimistic messages on create message mutation success.
    useEffect(() => {
        if (
            createMessageFetcher.data &&
            createMessageFetcher.state !== 'submitting'
        ) {
            const sentMessage = createMessageFetcher.data.message;
            // When we get the result of the create message mutation, replace
            // the optimistic message with the real message.
            setOptimisticMessages((messages) => {
                const newMessages: Message[] = [];
                let isReplaced = false;
                // Iterate from oldest to newest (reverse order).
                for (const message of [...messages].reverse()) {
                    if (isReplaced) {
                        newMessages.unshift(message);
                        continue;
                    }
                    // Find the corresponding optimistic message by verifying
                    // the message type, content, and similar timestamp.
                    const optimisticTimestamp = dayjs
                        .utc(message.Created)
                        .unix();
                    const sentTimestamp = dayjs.utc(sentMessage.Created).unix();
                    if (
                        message.isOptimistic &&
                        message.__typename === sentMessage.__typename &&
                        // Check content is the same.
                        message.Content === sentMessage.Content &&
                        // Check number of attachments are the same.
                        (message.__typename !== 'FileAttachmentMessage' ||
                            sentMessage.__typename !==
                                'FileAttachmentMessage' ||
                            message.Attachments.length ===
                                sentMessage.Attachments.length) &&
                        // Check the timestamp is within 10 seconds.
                        Math.abs(optimisticTimestamp - sentTimestamp) < 10
                    ) {
                        newMessages.unshift({
                            ...sentMessage,
                            isOptimistic: false,
                        });
                        isReplaced = true;
                    } else {
                        newMessages.unshift(message);
                    }
                }
                return newMessages;
            });
        }
    }, [createMessageFetcher]);

    // Update the conversation status on mark as read mutation success.
    useEffect(() => {
        if (
            markAsReadFetcher.data &&
            markAsReadFetcher.state !== 'submitting'
        ) {
            const updatedConversation = markAsReadFetcher.data.conversation;
            setConversation((prev) => {
                if (!prev) {
                    return prev;
                }
                const newConversation = {
                    ...prev,
                    Unread: updatedConversation.Unread,
                    UnreadMessageCount: updatedConversation.UnreadMessageCount,
                };
                cacheConversation(newConversation);
                return newConversation;
            });
        }
    }, [markAsReadFetcher, cacheConversation]);

    // Run callback and update message when delete message mutation finishes.
    useEffect(() => {
        if (deleteFetcher.data && deleteFetcher.state !== 'submitting') {
            const response = deleteFetcher.data.response;
            if (response.__typename === 'DeletedMessage') {
                updateMessage(response);
                latestOnDeleteSuccess.current?.(response);
            } else {
                latestOnDeleteError.current?.(response.Message);
            }
        }
    }, [
        deleteFetcher,
        latestOnDeleteSuccess,
        latestOnDeleteError,
        updateMessage,
    ]);

    return {
        /**
         * The conversation details. This is `null` if not fetched yet.
         */
        conversation,
        /**
         * The messages in the conversation ordered by earliest first. This includes
         * sent messages that are not in the conversation yet (optimistic UI).
         * Prefer to use this instead of the messages from the conversation object.
         */
        messages,
        /**
         * There are more messages to load.
         */
        hasMoreMessages: conversation?.Messages.pageInfo?.hasNextPage ?? false,
        /**
         * The conversation is currently being loaded.
         */
        isLoading: fetcher.state !== 'idle',
        /**
         * The create message fetcher is submitting.
         */
        isSubmitting: createMessageFetcher.state === 'submitting',
        /**
         * The mark as read fetcher is not idle.
         */
        isMarkAsReadBusy: markAsReadFetcher.state !== 'idle',
        /**
         * Refetch the conversation details.
         */
        refetch,
        /**
         * Load more messages by fetching the next page. This merges the new
         * messages with the existing messages.
         */
        loadMore,
        /**
         * Create a new message in the conversation.
         */
        createMessage,
        /**
         * Mark the conversation as read.
         */
        markAsRead,
        /**
         * Delete a message from the conversation.
         */
        deleteMessage,
    };
}
